/**
 * Keeps mention cards in normal flow and animates only their displacement.
 * Measuring local offsets lets reshare previews move independently inside an
 * already-moving conversation row.
 */
export class ConversationMotion {
    constructor() {
        this.positions = new Map();
        this.animations = new Map();
    }

    update(elements, reducedMotion = false) {
        const next = new Map(elements.map(element => [element, {
            x: element.offsetLeft, y: element.offsetTop
        }]));
        for (const [element, animation] of this.animations) {
            if (!next.has(element) || reducedMotion) {
                animation.cancel();
                this.animations.delete(element);
            }
        }
        for (const [element, position] of next) {
            const previous = this.positions.get(element);
            if (reducedMotion || !element.animate) continue;
            if (previous && previous.x === position.x && previous.y === position.y) continue;

            // A second batch can arrive before the first movement finishes. Start
            // from the current visual position, not the previous destination.
            const transform = getComputedStyle(element).transform;
            const matrix = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform) : null;
            const dx = previous ? previous.x - position.x + (matrix?.m41 || 0) : -16;
            const dy = previous ? previous.y - position.y + (matrix?.m42 || 0) : -12;
            const opacity = previous ? getComputedStyle(element).opacity : 0;
            this.animations.get(element)?.cancel();
            const animation = element.animate([
                {transform: `translate(${dx}px, ${dy}px)`, opacity},
                {transform: 'translate(0, 0)', opacity: 1}
            ], {duration: 800, easing: 'cubic-bezier(0.2, 0, 0, 1)'});
            this.animations.set(element, animation);
            animation.onfinish = () => {
                if (this.animations.get(element) === animation) this.animations.delete(element);
            };
        }
        this.positions = next;
    }

    get active() { return this.animations.size > 0; }

    finish() {
        // No fill is used: cancelling reveals the normal-flow destination now,
        // before callers measure where to scroll.
        this.animations.forEach(animation => animation.cancel());
        this.animations.clear();
    }

    dispose() {
        this.finish();
        this.positions.clear();
    }
}
